import { useCallback, useMemo } from "react";
import { useAIModelStore } from "../store/ai-model-store";
import { useAIModelBackend } from "./use-ai-model-backend";

export const useModelDialog = () => {
  const {
    isOpen,
    query,
    openModal,
    closeModal,
    updateQuery,
    selectModel: selectModelAction,
  } = useAIModelStore();

  // External override varsa backend fetch yapma (duplicate call önleme)
  const { externalModels } = useAIModelStore();
  const isExternalMode = Boolean(externalModels && externalModels.length > 0);
  const enableBackend = isOpen && !isExternalMode;

  // Backend hook'undan data ve fonksiyonları al - search ile
  const { models, loadMore, hasNextPage, isLoadingMore } = useAIModelBackend(
    enableBackend,
    query
  );

  // 🎯 Modal açıldığında manual fetch tetiklenir
  // useAIModelBackend hook'u zaten isOpen parametresi ile kontrol ediliyor

  // External override varsa onu kullan, yoksa backend verisi
  const baseModels =
    externalModels && externalModels.length > 0 ? externalModels : models;

  // Local search: External mode'da backend araması devre dışı olduğundan
  // baseModels üzerinde client-side filtre uygula
  const normalizedQuery = (query || "").trim().toLowerCase();
  const filteredModels = useMemo(() => {
    if (!isExternalMode) {
      // Backend mode: server-side filtering aktif; yine de güvenlik için basit client filter yok
      return baseModels;
    }
    if (!normalizedQuery) return baseModels;
    try {
      return baseModels.filter((m: any) => {
        const parts: string[] = [];
        if (m?.name) parts.push(String(m.name));
        if (m?.title) parts.push(String(m.title));
        if (m?.description)
          for (const d in m.description)
            if (d) parts.push(String(d));

        const haystack = parts.join(" ").toLowerCase();
        return haystack.includes(normalizedQuery);
      });
    } catch {
      return baseModels;
    }
  }, [baseModels, isExternalMode, normalizedQuery]);

  // Scroll handler - infinite scroll için
  const handleScroll = useCallback(
    (element: HTMLDivElement) => {
      if (!element) return;
      // External mode'da pagination devre dışı
      if (isExternalMode) return;

      const { scrollTop, scrollHeight, clientHeight } = element;
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        loadMore();
      }
    },
    [loadMore, hasNextPage, isLoadingMore, isExternalMode]
  );

  // Model selection with callback support
  const selectModel = useCallback(
    (model: any, onSelect?: (model: any) => void) => {
      selectModelAction(model);
      onSelect?.(model);
    },
    [selectModelAction]
  );

  return {
    // State
    isOpen,
    query,
    filteredModels,
    isExternalMode,

    // Actions
    openModal,
    closeModal,
    updateQuery,
    selectModel,
    handleScroll,

    // Computed
    hasMoreItems: hasNextPage,
    totalModels: models.length,
    filteredCount: filteredModels.length,
    isLoadingMore,
  };
};
